import { uploadService } from '@/services/uploadService'
import { Upload, UploadProps, message } from 'antd'
import { RcFile } from 'antd/es/upload'
import React from 'react'
import cls from 'classnames'
import styles from './AUpload.module.less'
import { useTranslation } from 'react-i18next'

export type Props = Pick<UploadProps, 'accept'> & {
  /** 默认单位: 1MB */
  maxSize?: number
  minDuration?: number
  maxDuration?: number
  minDurationErrorMsg?: string
  maxDurationErrorMsg?: string
  validateDuration?: (duration: number) => string | undefined
  onUploadChange?: (p: {
    status: 'error' | 'pending' | 'success'
    fileUrl: string
    fileId: number
    errMsg: string
    duration?: number
  }) => void
  children?: React.ReactNode
  className?: string
  testId?: string
  source: Models.FileUploadModule
  expectOrientation?: 'landscape' | 'portrait'
}
const COMPONENT_NAME = 'A-Upload'

const getVideoDuration = function (file: RcFile): Promise<{ duration: number, landscape: boolean }> {
  const video = document.createElement('video')
  const objectUrl = URL.createObjectURL(file)
  video.src = objectUrl
  video.setAttribute('style', 'position: fixed; left: -9999px; top: -9999px; z-index: -999;')
  document.body.appendChild(video)
  const cleanup = () => {
    URL.revokeObjectURL(objectUrl)
    if (video.parentNode) {
      document.body.removeChild(video)
    }
  }
  return new Promise((resolve, reject) => {
    video.addEventListener('loadedmetadata', () => {
      const rect = video.getBoundingClientRect()
      const videoWidth = rect.width
      const videoHeight = rect.height
      resolve({
        duration: video.duration,
        /** 视频是否是横屏 */
        landscape: videoWidth > videoHeight,
      })
      cleanup()
    })
    video.addEventListener('error', () => {
      cleanup()
      reject()
    })
  })
}

function AUpload(props: Props) {
  const { t } = useTranslation()
  const minDuration = props.minDuration || 10
  const maxDuration = props.maxDuration || 121
  const checkedDurationRef = React.useRef<number | undefined>(undefined)

  const beforeUpload = async (file: RcFile) => {
    if (file.size / 1024 / 1024 > (props.maxSize || 200)) {
      props.onUploadChange?.({
        status: 'error',
        fileUrl: '',
        fileId: 0,
        errMsg: t('dashboard.projectCreatePage.message.videoSizeOver200M'),
      })
      return false
    }
    try {
      const { duration, landscape } = await getVideoDuration(file)
      checkedDurationRef.current = duration
      const expectPortrait = props.expectOrientation === 'portrait'
      const orientationPass = expectPortrait ? !landscape : landscape
      if (!orientationPass) {
        props.onUploadChange?.({
          status: 'error',
          fileUrl: '',
          fileId: 0,
          errMsg: expectPortrait
            ? t('dashboard.projectCreatePage.message.videoPortrait')
            : t('dashboard.projectCreatePage.message.videoLandscape'),
        })
        return false
      }
      // 实际限制为 121 秒，提示文案保持 120 秒
      if (duration > maxDuration) {
        props.onUploadChange?.({
          status: 'error',
          fileUrl: '',
          fileId: 0,
          errMsg: props.maxDurationErrorMsg || t('dashboard.projectCreatePage.message.videoDurationOver60s'),
        })
        return false
      }
      if (!expectPortrait && duration < minDuration) {
        props.onUploadChange?.({
          status: 'error',
          fileUrl: '',
          fileId: 0,
          errMsg: props.minDurationErrorMsg || t('dashboard.projectCreatePage.message.videoDurationLessThan10s'),
        })
        return false
      }
      const customDurationError = props.validateDuration?.(duration)
      if (customDurationError) {
        props.onUploadChange?.({
          status: 'error',
          fileUrl: '',
          fileId: 0,
          errMsg: customDurationError,
        })
        return false
      }
    } catch {
      message.error(t('dashboard.projectCreatePage.message.videoFormatOrDurationError'))
      return false
    }
    return true
  }

  const customRequest = async (options: any) => {
    props.onUploadChange?.({ status: 'pending', fileUrl: '', fileId: 0, errMsg: '' })
    // let res: { downloadUrl: string, error: string, fileId: number } = { downloadUrl: '', error: '', fileId: -1 }
    // const TEST_CORP_ID = '1826898971837228033'
    // if (userService.userInfo?.corpId === TEST_CORP_ID) {
    //   const res1 = await uploadService.sliceUploadVideoFile({
    //     file: options.file,
    //     module: props.source,
    //   })
    //   res = res1
    // } else {
    //   const res2 = await uploadService.uploadVideoFile({
    //     file: options.file,
    //     module: props.source,
    //   })
    //   res = res2
    // }
    // const { downloadUrl, error, fileId } = res
    const { downloadUrl, error, fileId } = await uploadService.sliceUploadVideoFile({
      file: options.file,
      module: props.source,
    })
    if (error) {
      props.onUploadChange?.({ status: 'error', fileUrl: '', fileId: 0, errMsg: error })
      message.error(error)
      return
    }
    props.onUploadChange?.({
      status: 'success',
      fileUrl: downloadUrl,
      fileId,
      errMsg: '',
      duration: checkedDurationRef.current,
    })
  }

  return (
    <Upload
      {...(props.testId ? { 'data-testid': props.testId } : {})}
      className={cls(styles[COMPONENT_NAME], props.className)}
      accept={props.accept}
      showUploadList={false}
      multiple={false}
      customRequest={customRequest}
      beforeUpload={beforeUpload}
    >
      {props.children}
    </Upload>
  )
}

export default AUpload
